import { CustomDomainSchema } from '@hashnode/database-schema'
import { getLogger, Logger } from '@hashnode/file-parsing'
import { createDocsGuideUrl } from '@hashnode/urls'
import {
	checkForDuplicatePaths,
	checkForDuplicateSlugs,
	getWebEnvironment,
	isNonNullable,
} from '../../helpers'
import {
	DuplicatedPath,
	GuideErrorMap,
	GuideFileErrorMap,
	MissingFile,
	ProjectErrors,
} from '../../types'
import { generateSlug } from '../generate-slug'
import { mapVersionPages } from '../map-version-pages'
import { validateProjectStructure } from './check-folder-structure'
import {
	checkMissingFilesInSidebar,
	checkMissingReferences,
} from './check-missing-files-in-content'
import { validatePageContent } from './check-page'
import { checkRelativeLinks } from './check-relative-links-in-content'
import { checkForErrors, processErrors, processWarnings } from './process-errors'

// TODO: Get all pages that are sourced from Hashnode to check for duplicates

export type DocsProjectCheckType = {
	id: string
	subdomain: string
	customDomain?: Pick<NonNullable<CustomDomainSchema>, 'domain' | 'status'>
	guides: {
		id: string
		slug: string
		provider: 'GITHUB' | 'HASHNODE'
	}[]
}

/**
 * Check the project for any errors. If there is an error with the project structure, or in the config.ts file, this function will throw an error.
 * Otherwise, it will return an object with any errors that were found.
 */
export function checkProject({
	exitingProject,
	folderPath,
	logger = getLogger(),
}: {
	exitingProject: DocsProjectCheckType
	folderPath: string
	logger?: Logger
}): ProjectErrors {
	/**
	 * Check if the project structure is valid and get the configuration errors (e.g. missing config.ts files, wrong schemas, missing folders, etc.)
	 * This is done by:
	 * 1. Walking the folder structure.
	 * 2. Validating the folder structure.
	 * 3. Getting the guide information.
	 * 4. Validating the guide information.
	 */

	const { guideInformation, errorMap: configurationErrors } = validateProjectStructure({
		rootDir: folderPath,
		ignoreHashnodeSettingsFile: true,
	})

	if (configurationErrors.size > 0) {
		logger.debug('Configuration errors found. Continuing with valid versions')
	}

	/**
	 * Check for duplicated slugs in the project.
	 * This is done by:
	 * 1. Getting the slugs from the guideInformation.
	 * 2. Getting the slugs from the exitingProject.
	 * 3. Checking for duplicates in the slugs.
	 */

	const guideSlugs = guideInformation
		.map(({ guideConfig, guideFolderPath }) => {
			if (guideConfig?.settings.slug) {
				return { slug: guideConfig?.settings.slug, folderPath: guideFolderPath }
			}

			return null
		})
		.filter(isNonNullable)

	const existingGuideSlugs = exitingProject.guides
		.filter((guide) => guide.provider !== 'GITHUB')
		.map((guide) => ({
			slug: guide.slug,
			url: createDocsGuideUrl({
				project: {
					customDomain: exitingProject.customDomain
						? {
								createdAt: new Date(),
								domain: exitingProject.customDomain.domain,
								status: exitingProject.customDomain.status,
							}
						: undefined,
					subdomain: exitingProject.subdomain,
				},
				guideSlug: guide.slug,
				environment: getWebEnvironment(),
			}),
		}))

	const duplicatedSlugs = checkForDuplicateSlugs([...guideSlugs, ...existingGuideSlugs])

	// Enhance the valid guides and versions with all the pages and sidebar item paths

	const guideInformationWithAllPages = guideInformation.map(mapVersionPages)

	/**
	 * Check page content, missing files and relative links
	 */

	const missingFilesInSidebar = checkMissingFilesInSidebar(guideInformationWithAllPages)

	const { contentErrors, guides: guidesWithPageContent } = validatePageContent(
		guideInformationWithAllPages,
	)

	const missingFileReferences = checkMissingReferences(guidesWithPageContent)

	// Combine missingFilesInSidebar and missingFileReferences into missingFiles
	function combineMissingFiles(
		missingFilesInSidebar: GuideFileErrorMap<MissingFile>,
		missingFileReferences: GuideFileErrorMap<MissingFile>,
	): GuideFileErrorMap<MissingFile> {
		const combinedMissingFiles: GuideFileErrorMap<MissingFile> = new Map()

		function updateMissingFiles(sourceMap: GuideFileErrorMap<MissingFile>) {
			sourceMap.forEach((versionMap, guidePath) => {
				const guideMap = combinedMissingFiles.get(guidePath) || new Map()
				versionMap.forEach((fileMap, versionPath) => {
					const versionFileMap = guideMap.get(versionPath) || new Map()
					fileMap.forEach((missingFiles, filePath) => {
						const existingMissingFiles = versionFileMap.get(filePath) || []
						versionFileMap.set(filePath, [...existingMissingFiles, ...missingFiles])
					})
					guideMap.set(versionPath, versionFileMap)
				})
				combinedMissingFiles.set(guidePath, guideMap)
			})
		}

		updateMissingFiles(missingFilesInSidebar)
		updateMissingFiles(missingFileReferences)

		return combinedMissingFiles
	}

	const missingFiles = combineMissingFiles(missingFilesInSidebar, missingFileReferences)

	const relativeLinks = checkRelativeLinks(guidesWithPageContent)

	const duplicatedPaths: GuideErrorMap<DuplicatedPath> = new Map()

	guidesWithPageContent.forEach(({ versionInformation }) => {
		return versionInformation.map(({ existingFiles, versionFolderPath, config }) => {
			// Check for duplicated paths in the existing files
			const duplicatedPathsPerVersion = checkForDuplicatePaths(
				existingFiles.map(({ filePath, slug }) => ({
					filePath,
					path: slug ?? '',
				})),
			)
			let duplicatedSectionPaths: DuplicatedPath[] = []
			// Check for duplicated paths in the sidebar sections
			if ('sidebar' in config) {
				duplicatedSectionPaths = checkForDuplicatePaths(
					config.sidebar
						.filter((item) => item.type === 'section')
						.map((item) => ({
							filePath: `${versionFolderPath}/config.ts`,
							path: generateSlug({ slug: item.slug, title: item.label }),
						})),
				)
			}

			duplicatedPaths.set(versionFolderPath, [
				...duplicatedPathsPerVersion,
				...duplicatedSectionPaths,
			])
		})
	})

	const rawErrors = {
		configurationErrors,
		duplicatedSlugs,
		contentErrors,
		duplicatedPaths,
		missingFiles,
	}

	const rawWarnings = {
		relativeLinks,
	}

	return {
		rawErrors,
		rawWarnings,
		hasErrors: checkForErrors(rawErrors),
		errors: processErrors(rawErrors),
		warnings: processWarnings(rawWarnings),
	}
}
